import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const UserResumeSchema = z
    .object({
        id: z.number(),
        email: z.string().trim(),
    })
    .readonly();

type UserResume = z.output<typeof UserResumeSchema>;

const SpatialLevelResumeSchema = z
    .object({
        id: zIdSchema,
        name: z.string().trim(),
        toLabel: z.string().trim(),
        users: z.array(UserResumeSchema),
    })
    .brand<"SpatialLevelResume">();

export type SpatialLevelResumeInputProps = z.input<
    typeof SpatialLevelResumeSchema
>;
type SpatialLevelResumeProps = z.output<typeof SpatialLevelResumeSchema>;

export class SpatialLevelResume {
    private constructor(
        private readonly props: Readonly<SpatialLevelResumeProps>,
    ) {}

    get id(): Id {
        return this.props.id;
    }

    get name(): string {
        return this.props.name;
    }

    get toLabel(): string {
        return this.props.toLabel;
    }

    get users(): UserResume[] {
        return this.props.users;
    }

    static empty(): SpatialLevelResume {
        return SpatialLevelResume.create({
            id: 0,
            name: "",
            toLabel: "",
            users: [],
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<SpatialLevelResumeInputProps>>,
    ): Result<SpatialLevelResumeProps, z.ZodError<SpatialLevelResumeProps>> {
        const result = SpatialLevelResumeSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<SpatialLevelResumeInputProps>>,
    ): Result<
        SpatialLevelResumeProps,
        PartialValid<typeof SpatialLevelResume>
    > {
        const result = partialSafeParse(SpatialLevelResumeSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<SpatialLevelResumeInputProps>,
    ): Result<SpatialLevelResume, z.ZodError<SpatialLevelResumeProps>> {
        return SpatialLevelResume.validate(props).map(
            (validatedProps) => new SpatialLevelResume(validatedProps),
        );
    }

    static createWithRecovery(
        props: SpatialLevelResumeInputProps,
    ): Result<SpatialLevelResume, ModelError<typeof SpatialLevelResume>> {
        return SpatialLevelResume.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<SpatialLevelResumeProps> = {
                    id: error.validProps.id,
                    name: "",
                    toLabel: "",
                    users: [],
                };

                const recoveryProps: Unbrand<SpatialLevelResumeProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof SpatialLevelResume>(
                    new SpatialLevelResume(
                        recoveryProps as SpatialLevelResumeProps,
                    ),
                    error.errors,
                );
            })
            .map((validatedProps) => new SpatialLevelResume(validatedProps));
    }

    toCreateProps(): SpatialLevelResumeInputProps {
        return {
            id: this.props.id.toCreateProps(),
            name: this.props.name,
            toLabel: this.props.toLabel,
            users: this.props.users.map((user) => ({
                id: user.id,
                email: user.email,
            })),
        };
    }
}

const SpatialLevelsResumeSchema = z
    .object({
        spatialLevelsResume: z.array(
            z.custom<SpatialLevelResume>(
                (val) => val instanceof SpatialLevelResume,
                { error: "Invalid SpatialLevelResume instance" },
            ),
        ),
        count: z.number().min(0),
    })
    .brand<"SpatialLevels">();

export type SpatialLevelsResumeInputProps = z.input<
    typeof SpatialLevelsResumeSchema
>;
export type SpatialLevelsResumeProps = z.output<
    typeof SpatialLevelsResumeSchema
>;

export class SpatialLevelsResume {
    private constructor(
        private readonly props: Readonly<SpatialLevelsResumeProps>,
    ) {}

    get spatialLevels(): readonly Readonly<SpatialLevelResume>[] {
        return this.props.spatialLevelsResume;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): SpatialLevelsResume {
        return SpatialLevelsResume.create({
            spatialLevelsResume: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<SpatialLevelsResumeInputProps>>,
    ): Result<SpatialLevelsResumeProps, z.ZodError> {
        const result = SpatialLevelsResumeSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<SpatialLevelsResumeInputProps>,
    ): Result<SpatialLevelsResume, z.ZodError<SpatialLevelsResumeProps>> {
        return SpatialLevelsResume.validate(props).map(
            (validatedProps) => new SpatialLevelsResume(validatedProps),
        );
    }
}

export const SpatialLevelUpdateSchema = z.object({
    id: z.number().positive(),
    name: z.string().trim().min(1),
});
